import { Component, OnInit, signal } from '@angular/core';
import { environment } from '@env/environment';
import { ReportGeneratorTokenService } from './report-generator-token.service';
import { catchError, map } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { SpinnerLoadingIndicatorComponent } from '@cbre/components';
import { TranslationModule } from '@miq/utils';
import { ReactEmbeddedComponent } from '@shared/components/react-embed/react-embedded.component';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { ErrorPageStandaloneComponent } from '@shared/components/error-page/error-page.component-standalone';
import { ReactEmbeddedVersionService } from '@shared/components/react-embed/react-embedded.service';
import { forkJoin, of } from 'rxjs';

@Component({
  selector: 'miq-report-generator',
  templateUrl: './report-generator.component.html',
  styleUrls: ['./report-generator.component.scss'],
  imports: [
    CommonModule,
    SpinnerLoadingIndicatorComponent,
    TranslationModule,
    ReactEmbeddedComponent,
    ErrorPageStandaloneComponent,
  ],
})
export class ReportGeneratorComponent implements OnInit {
  $isLoading = signal<boolean>(true);
  $hasError = signal<boolean>(false);
  $props = signal<any>(null);

  baseUrl = environment.ReportGenerator.FEPrefix;
  jsPath = null;
  cssPath = null;

  constructor(
    private tokenService: ReportGeneratorTokenService,
    private userInfo: UserInfoResolver,
    private reactVersionService: ReactEmbeddedVersionService
  ) {}

  private getScriptsUrls = (baseUrl: string) => {
    const errorObj = { mainjsUrl: null, maincssUrl: null };
    return this.reactVersionService.getVersion(baseUrl).pipe(
      catchError(_error => of({ ...errorObj })),
      map((appInfo: any) => {
        const { mainjs, maincss } = appInfo || {};
        if (!mainjs || !maincss) {
          return { ...errorObj };
        }
        const relativeJsPath = mainjs.startsWith('/') ? mainjs : `/${mainjs}`;
        const relativeCssPath = maincss.startsWith('/') ? maincss : `/${maincss}`;
        return { mainjs: relativeJsPath, maincss: relativeCssPath };
      })
    );
  };

  private getToken = () => {
    return this.tokenService.getToken().pipe(
      catchError(_error => {
        return of(null);
      })
    );
  };

  private setAppIntegrationConfig() {
    const configs$ = forkJoin({
      urls: this.getScriptsUrls(this.baseUrl),
      tokenResponse: this.getToken(),
    });

    configs$.subscribe(({ urls, tokenResponse }) => {
      if (!urls.mainjs || !urls.maincss || !tokenResponse?.token) {
        this.$hasError.set(true);
        this.$isLoading.set(false);
        return;
      }

      // Set the paths for the React app scripts
      this.jsPath = urls.mainjs;
      this.cssPath = urls.maincss;

      // Set the props for the React app
      const user = this.userInfo.resolve();
      this.$props.set({
        apiToken: tokenResponse.token,
        parentApp: 'MarketIQ',
        userId: user?.id,
        initialRoute: `/signin?jwt=${tokenResponse.token}`,
      });
      this.$hasError.set(false);

      this.$isLoading.set(false);
    });
  }

  ngOnInit() {
    if (!this.baseUrl) {
      this.$hasError.set(true);
      this.$isLoading.set(false);
      return;
    }

    this.setAppIntegrationConfig();
  }

  onErrorWhenEmbedding() {
    this.$hasError.set(true);
    this.$isLoading.set(false);
  }
}
